/**
 * LoadedBooking — the booking detail page's loaded body: header + two-column
 * layout (BON-145). Split out of `BookingDetailPage.tsx` so each file stays
 * under the max-lines-per-function cap and keeps `react/no-multi-comp`'s
 * one-component-per-file rule (this uses hooks, so unlike `statusPill` it
 * can't collapse into a plain lowercase helper).
 *
 * Action gating lives in `bookingDetailModel`; the chain lives in
 * `RebookingChain`; the presentational cards in `BookingDetailCards`. Rebook
 * opens `RebookBookingDrawer` (BON-146) and Refund opens `RefundBookingDrawer`
 * (BON-147/BON-335); both report back through `onBookingActionCompleted` so the
 * status pill, seats, chain and audit timeline reflect the change without a
 * full-page loading flash.
 *
 * `Activity` (`BookingAuditTimeline`) lives in the LEFT column beneath
 * `AttendeesCard` (manual-testing follow-up to BON-335), not the right rail —
 * the same JSX placement drives both breakpoints, since the phone layout
 * collapses the grid to one column and stacks the left column's children
 * before the right column's, source order.
 */
import type { BookingDetail } from '../../api/bookingsV2';
import { useViewport } from '../../hooks/useViewport';

import { AttendeesCard } from './AttendeesCard';
import { BookingActionDialogs } from './BookingActionDialogs';
import { BookingActions } from './BookingActions';
import { BookingAuditTimeline } from './BookingAuditTimeline';
import { DetailHeader } from './BookingDetailHeader';
import { BookingReferencesCard } from './BookingReferencesCard';
import { CustomerCard } from './CustomerCard';
import { GiftPassesEnabledProvider } from './GiftPassesEnabledProvider';
import { MobileActionBar } from './MobileActionBar';
import { PaymentCard } from './PaymentCard';
import { RebookingChain } from './RebookingChain';
import { useBookingActionGating } from './useBookingActionGating';
import { WorkshopSummaryCard } from './WorkshopSummaryCard';

export function LoadedBooking({ booking, reload }: { booking: BookingDetail; reload: () => void }) {
  const { isPhone, isDesktop } = useViewport();
  const g = useBookingActionGating(booking, reload);

  return (
    <GiftPassesEnabledProvider>
      <div className={`w-full ${isPhone ? 'pb-28' : 'pb-8'}`}>
        <DetailHeader booking={booking} isDesktop={isDesktop} />

        {/* Body */}
        <div className="grid grid-cols-1 gap-8 px-6 py-8 sm:px-8 md:grid-cols-3 lg:px-12">
          <div className="space-y-8 md:col-span-2">
            <WorkshopSummaryCard booking={booking} desktop={isDesktop} />
            {!isDesktop && <CustomerCard booking={booking} />}
            <AttendeesCard booking={booking} />
            <BookingAuditTimeline bookingId={booking.id} refreshToken={g.activityRefreshToken} />
          </div>

          <div className="space-y-8">
            {!isPhone && (
              <BookingActions
                status={booking.status}
                canRebook={g.canRebook}
                canOverrideCutoff={g.canOverrideCutoff}
                cutoffPassed={g.cutoffPassed}
                canRetire={g.canRetire}
                isComp={booking.isComp}
                amountPaidCents={booking.money.amountPaidCents}
                workshopEndsAt={booking.workshop.endsAt}
                canResendConfirmation={g.canResendConfirmation}
                onRebook={g.openRebook}
                onRetire={g.onRetire}
                onResendConfirmation={g.openResendConfirmation}
              />
            )}
            <PaymentCard booking={booking} />
            {/* `?? []` for the same separate-deploys reason as `cutoffPassed` above —
              a bare `.length` on an older API's payload white-screens the page. */}
            <BookingReferencesCard
              links={booking.links ?? []}
              giftCards={booking.giftCardsRedeemed ?? []}
            />
            <RebookingChain chain={booking.chain} />
          </div>
        </div>

        {isPhone && (
          <MobileActionBar
            status={booking.status}
            canRebook={g.canRebook}
            canOverrideCutoff={g.canOverrideCutoff}
            cutoffPassed={g.cutoffPassed}
            canRetire={g.canRetire}
            isComp={booking.isComp}
            amountPaidCents={booking.money.amountPaidCents}
            workshopEndsAt={booking.workshop.endsAt}
            canResendConfirmation={g.canResendConfirmation}
            onRebook={g.openRebook}
            onRetire={g.onRetire}
            onResendConfirmation={g.openResendConfirmation}
          />
        )}

        <BookingActionDialogs booking={booking} g={g} />
      </div>
    </GiftPassesEnabledProvider>
  );
}
